import React, { useEffect, useState } from "react";
import "./SessionDetailsPage.css";
import "../../../patientFullView/patientFullView.css";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Widgets from "../../../../widgets";
import Icons from "../../../../Assets/Icons";
import strings from "../../../../utils/strings";
import jsPDF from "jspdf";
import { getNurseSessionDetails, getRequestSessions, scheduleNursingRequest, getNurseRequestDetails } from "../../../../modules/NursignCare/ManageRequest/manageRequestThunk";
import { getNurseDetails } from "../../../../modules/NursignCare/ManageNurse/ManageNurseThunk";
import ToastPopup from "../../../../widgets/Toast/toastPopup";
import { buildFileUrl, getFileType } from "../../../../utils/fileHelpers";
import { openSecureFileUrl } from "../../../../utils/secureFilePreview";
import ChatWidget from "../../../Chat/ChatWidget";

const DAY_KEYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Convert 12h {h, m, p} to minutes-since-midnight for comparison
const to24hMinutes = (h, m, p) => {
  let hrs = parseInt(h, 10);
  if (p === "PM" && hrs !== 12) hrs += 12;
  if (p === "AM" && hrs === 12) hrs = 0;
  return hrs * 60 + parseInt(m, 10);
};

// Inline time picker: Hour : Minute  AM/PM
// minMinutes: optional minutes-since-midnight floor — options before this are disabled (used to block past times for "today")
const TimePicker = ({ value, onChange, minMinutes = null }) => {
  const parse = (v) => {
    if (!v) return { h: "10", m: "00", p: "AM" };
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) {
      const d = new Date(v);
      const hrs = d.getHours();
      return { h: String(hrs % 12 || 12).padStart(2, "0"), m: String(d.getMinutes()).padStart(2, "0"), p: hrs >= 12 ? "PM" : "AM" };
    }
    const match = v.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (match) return { h: match[1].padStart(2, "0"), m: match[2], p: match[3].toUpperCase() };
    return { h: "10", m: "00", p: "AM" };
  };
  const { h, m, p } = parse(value);
  const upd = (nh, nm, np) => onChange(`${nh}:${nm} ${np}`);
  const sel = {
    className: "sd-time-select",
    style: { border: "1px solid var(--box-border)", borderRadius: "0.6rem", padding: "0.4rem 0.6rem", fontSize: "1.4rem", background: "var(--main-bg)", cursor: "pointer" },
  };
  const isDisabled = (nh, nm, np) => minMinutes !== null && to24hMinutes(nh, nm, np) < minMinutes;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
      <select {...sel} value={h} onChange={(e) => upd(e.target.value, m, p)}>
        {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0")).map((v) => (
          <option key={v} value={v} disabled={isDisabled(v, m, p)}>
            {v}
          </option>
        ))}
      </select>
      <span style={{ fontWeight: 700 }}>:</span>
      <select {...sel} value={m} onChange={(e) => upd(h, e.target.value, p)}>
        {["00", "15", "30", "45"].map((v) => (
          <option key={v} value={v} disabled={isDisabled(h, v, p)}>
            {v}
          </option>
        ))}
      </select>
      <select {...sel} value={p} onChange={(e) => upd(h, m, e.target.value)}>
        <option value="AM" disabled={isDisabled(h, m, "AM")}>
          AM
        </option>
        <option value="PM" disabled={isDisabled(h, m, "PM")}>
          PM
        </option>
      </select>
    </div>
  );
};

const SessionDetailsPage = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("vn") || i18n.language?.startsWith("vi") ? "vi" : "en";
  const nav = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const queryParams = new URLSearchParams(location.search);
  const sessionId = queryParams.get("id");
  const requestId = queryParams.get("requestId");
  const from = queryParams.get("from") || "upcoming";

  const { sessionDetails, requestSessions, requestDetails } = useSelector((state) => state.manageRequest);
  const [localSession, setLocalSession] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);

  const breadcrumbs = ["nursingCare", "manageRequests", "sessionDetails"];
  const backTab = from === "scheduled" ? "scheduled" : from === "past" ? "past" : "upcoming";
  const paths = [strings.navigation.manageNurseReq, `${strings.navigation.manageNurseReq}?activeList=${backTab}`, strings.navigation.viewSessionDetails];

  // Editable check-in / check-out (stored as "HH:MM AM/PM")
  // Check-in / check-out (upcoming/past)
  const [checkInTime, setCheckInTime] = useState("10:00 AM");
  const [checkOutTime, setCheckOutTime] = useState("01:00 PM");

  // Schedule inputs for PUT /request/schedule/:id (scheduled tab)
  const [schedStartDate, setSchedStartDate] = useState("");
  const [schedEndDate, setSchedEndDate] = useState("");
  const [schedStartTime, setSchedStartTime] = useState("10:00 AM");
  const [schedEndTime, setSchedEndTime] = useState("01:00 PM");

  // Today's date (YYYY-MM-DD) — used as the min for date inputs so past dates can't be selected
  const todayISO = new Date().toISOString().split("T")[0];
  // Current minutes-since-midnight — used to disable past times when the selected date is today
  const nowMinutes = new Date().getHours() * 60 + new Date().getMinutes();

  // Frequency map — 7 booleans [Mon, Tue, Wed, Thu, Fri, Sat, Sun]
  const [frequencyDays, setFrequencyDays] = useState(Array(7).fill(false));

  // Nurse details fetched from nurse API
  const [nurseDetails, setNurseDetails] = useState(null);
  const [nurseLoading, setNurseLoading] = useState(false);

  const [sessionRating, setSessionRating] = useState(null);
  // Nurse ID to open in the chat widget when "Chat with Nurse" is clicked
  const [chatNurseId, setChatNurseId] = useState(null);

  // Fetch and store session/request data in local state
  useEffect(() => {
    setLocalSession(null);
    setSessionLoading(true);

    if (sessionId) {
      // Upcoming / Past: fetch full session by ID
      dispatch(getNurseSessionDetails({ id: sessionId })).then((r) => {
        if (r.meta.requestStatus === "fulfilled") setLocalSession(r.payload?.data?.session || null);
        setSessionRating(r.payload?.data?.rating || null);
        setSessionLoading(false);
      });
    } else if (requestId) {
      // Scheduled: fetch request details, populate form from request.services[0]
      dispatch(getNurseRequestDetails({ id: requestId })).then((r) => {
        if (r.meta.requestStatus === "fulfilled") {
          const req = r.payload?.data?.request || null;
          if (req) {
            const svc = req.services?.[0];
            setLocalSession({
              _id: null,
              nursingRequest: { _id: req._id, nursingRequestID: req.nursingRequestID },
              service: svc?.service || null,
              subService: svc?.subService || null,
              serviceItem: svc?.serviceItem || null,
              startTime: svc?.startTime || null,
              endTime: svc?.endTime || null,
              date: req.startDate || null,
              status: req.status,
              checkInTime: null,
              checkOutTime: null,
              frequencyMap: req.frequencyMap || null,
            });
          }
        }
        setSessionLoading(false);
      });
    }
  }, [sessionId, requestId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Always use local state
  const session = localSession;

  // If start date is today and the chosen start time is already in the past, bump it to the next quarter-hour
  useEffect(() => {
    if (schedStartDate !== todayISO) return;
    const match = schedStartTime.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return;
    if (to24hMinutes(match[1], match[2], match[3].toUpperCase()) < nowMinutes) {
      const next = Math.ceil(nowMinutes / 15) * 15;
      let h24 = Math.floor(next / 60) % 24;
      const m = next % 60;
      const period = h24 >= 12 ? "PM" : "AM";
      const h12 = h24 % 12 || 12;
      setSchedStartTime(`${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${period}`);
    }
  }, [schedStartDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch full request details for the fullDetails UI
  useEffect(() => {
    const reqId = session?.nursingRequest?._id;
    if (reqId) dispatch(getNurseRequestDetails({ id: reqId }));
  }, [session?.nursingRequest?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync check-in / check-out and frequency from session data
  useEffect(() => {
    if (!session) return;
    setCheckInTime(session.checkInTime || "10:00 AM");
    setCheckOutTime(session.checkOutTime || "01:00 PM");
    // Seed frequency map if it exists and is 7 chars
    if (session.frequencyMap && session.frequencyMap.length === 7) {
      setFrequencyDays(session.frequencyMap.split("").map((c) => c === "1"));
    }
  }, [session?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Extract nurse from session (nurse is an array)
  const nurseEntry = Array.isArray(session?.nurse) ? session.nurse[0] : session?.nurse;
  const nurseId = nurseEntry?._id;

  // Seed nurseDetails from embedded session data immediately (has name, ID, phone, gender, photo)
  // Then fetch full nurse API for email / address
  useEffect(() => {
    if (!nurseEntry) return;
    // Seed immediately from session data
    setNurseDetails(
      (prev) =>
        prev || {
          firstName: nurseEntry.firstName,
          lastName: nurseEntry.lastName,
          employeeID: nurseEntry.employeeID,
          phoneNumber: nurseEntry.phoneNumber,
          countryCode: nurseEntry.countryCode,
          gender: nurseEntry.gender,
          profilePhoto: nurseEntry.profilePhoto,
        },
    );
    // Fetch full details for email + address
    if (nurseId) {
      setNurseLoading(true);
      dispatch(getNurseDetails(nurseId))
        .then((result) => {
          if (result.meta.requestStatus === "fulfilled") {
            const data = result.payload?.data?.nurse || result.payload?.nurse || result.payload;
            if (data) setNurseDetails(data);
          }
        })
        .finally(() => setNurseLoading(false));
    }
  }, [nurseId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Detect ISO date strings ("2026-06-01T04:30:00.000Z")
  const isISO = (s) => s && /^\d{4}-\d{2}-\d{2}T/.test(s);

  // Format date — handles ISO and pre-formatted ("23 Jun 2026 at 10:00 AM")
  const formatSessionDate = (s) => {
    if (!s) return "-";
    if (isISO(s)) {
      const d = new Date(s);
      return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    }
    return s.includes(" at ") ? s.split(" at ")[0] : s;
  };

  // Format time — handles ISO and pre-formatted
  const formatSessionTime = (s) => {
    if (!s) return "-";
    if (isISO(s)) {
      const d = new Date(s);
      return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
    }
    return s.includes(" at ") ? s.split(" at ")[1] : s;
  };

  // Duration — works for both ISO and "HH:MM AM/PM" strings
  const calcDuration = (start, end) => {
    if (!start || !end) return "";
    try {
      if (isISO(start) && isISO(end)) {
        const diff = Math.round((new Date(end) - new Date(start)) / 60000);
        if (diff <= 0) return "";
        const h = Math.floor(diff / 60),
          m = diff % 60;
        return h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
      }
      const extract = (s) => (s.includes(" at ") ? s.split(" at ")[1] : s);
      const toMins = (str) => {
        const [time, period] = str.trim().split(" ");
        const [h, m] = time.split(":").map(Number);
        let hrs = h;
        if (period?.toUpperCase() === "PM" && h !== 12) hrs += 12;
        if (period?.toUpperCase() === "AM" && h === 12) hrs = 0;
        return hrs * 60 + (m || 0);
      };
      const diff = toMins(extract(end)) - toMins(extract(start));
      if (diff <= 0) return "";
      const h = Math.floor(diff / 60),
        m = diff % 60;
      return h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
    } catch {
      return "";
    }
  };

  // Nurse name from session data (available before full fetch)
  const nurseNameFromSession = nurseEntry ? `${nurseEntry.firstName?.en || nurseEntry.firstName || ""} ${nurseEntry.lastName?.en || nurseEntry.lastName || ""}`.trim() : "";

  const displayNurseName = nurseDetails
    ? `${nurseDetails.firstName?.en || nurseDetails.firstName || ""} ${nurseDetails.lastName?.en || nurseDetails.lastName || ""}`.trim()
    : nurseNameFromSession || "-";

  // Convert "10:00 AM" / ISO → "10:00" (24h) for API
  const to24h = (s) => {
    if (!s) return "10:00";
    if (isISO(s)) {
      const d = new Date(s);
      return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    }
    const [time, period] = s.trim().split(" ");
    const [hStr, mStr] = time.split(":");
    let h = parseInt(hStr);
    if (period?.toUpperCase() === "PM" && h !== 12) h += 12;
    if (period?.toUpperCase() === "AM" && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:${mStr || "00"}`;
  };

  // Extract "YYYY-MM-DD" from ISO or formatted date string
  const toISODate = (s) => {
    if (!s) return "";
    if (isISO(s)) return s.split("T")[0];
    try {
      const d = new Date(s);
      if (!isNaN(d)) return d.toISOString().split("T")[0];
    } catch {}
    return "";
  };

  // 7-digit preview (Mon-Sun) for display only
  const frequencyMapStr = frequencyDays.map((d) => (d ? "1" : "0")).join("");

  // Generate frequencyMap whose length = number of days in date range
  // Each char = whether that specific date's weekday is selected
  // DAYS order: Mon(0)..Sun(6); JS getDay(): 0=Sun,1=Mon..6=Sat
  const buildFrequencyMap = (start, end) => {
    if (!start || !end) return frequencyMapStr || "1";
    const s = new Date(start),
      e = new Date(end);
    let result = "";
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      const jsDay = d.getDay(); // 0=Sun..6=Sat
      const idx = jsDay === 0 ? 6 : jsDay - 1; // convert to Mon(0)..Sun(6)
      result += frequencyDays[idx] ? "1" : "0";
    }
    // If user selected no days, default all to "1" (every day active)
    return result.length > 0 ? (result.includes("1") ? result : result.replace(/0/g, "1")) : "1";
  };

  const handleSave = () => {
    // For scheduled: use requestId from URL; for upcoming/past: use session's nursing request ID
    const reqId = requestId || session?.nursingRequest?._id;
    if (!reqId) {
      ToastPopup.error(t("requestIdNotFound"));
      return;
    }

    const svc = session?.service?._id || (typeof session?.service === "string" ? session.service : "");
    const subSvc = session?.subService?._id || (typeof session?.subService === "string" ? session.subService : "");
    const svcItem = session?.serviceItem?._id || (typeof session?.serviceItem === "string" ? session.serviceItem : "");

    // For scheduled: use user-entered dates/times; for others: derive from session
    const startDate = from === "scheduled" ? schedStartDate : toISODate(session?.date || session?.startTime);
    const endDate = from === "scheduled" ? schedEndDate : toISODate(session?.date || session?.endTime) || startDate;
    const startTime = from === "scheduled" ? to24h(schedStartTime) : to24h(session?.startTime);
    const endTime = from === "scheduled" ? to24h(schedEndTime) : to24h(session?.endTime);

    if (from === "scheduled" && (!schedStartDate || !schedEndDate)) {
      ToastPopup.error(t("selectStartEndDates"));
      return;
    }

    const body = {
      services: [
        {
          service: svc,
          subService: subSvc,
          serviceItem: svcItem,
          startTime,
          endTime,
        },
      ],
      startDate,
      endDate,
      frequencyMap: from === "scheduled" ? buildFrequencyMap(schedStartDate, schedEndDate) : frequencyMapStr || "1",
    };

    dispatch(scheduleNursingRequest({ requestId: reqId, body })).then((result) => {
      if (result.meta.requestStatus === "fulfilled") {
        ToastPopup.success(t("requestScheduled"));
        nav(`${strings.navigation.manageNurseReq}?activeList=${backTab}`);
      } else {
        ToastPopup.error(result.error?.message || t("failedToSchedule"));
      }
    });
  };

  // Build request data for fullDetails UI (same structure as ApprovedRequestDetails)
  const addr = requestDetails?.address;
  const fmtAddr = (a) => (a ? [a.street, a.ward, a.city, a.province, a.postalCode].filter(Boolean).join(", ") : "");
  const reqData = {
    patientName: requestDetails?.patient?.fullName || "",
    profilePhoto: requestDetails?.patient?.profilePhoto || "",
    gender: requestDetails?.patient?.gender || "",
    dob: requestDetails?.patient?.dateOfBirth || "",
    hn: requestDetails?.patient?.hnNumber || "",
    phone: requestDetails?.patient?.countryCode ? `+${requestDetails.patient.countryCode} ${requestDetails.patient.phoneNumber}` : requestDetails?.patient?.phoneNumber || "",
    email: requestDetails?.patient?.email || "",
    insuranceCategory: requestDetails?.patient?.insuranceCategory || "",
    address: fmtAddr(addr),
    zone: requestDetails?.distanceInKm ? `${requestDetails.distanceInKm} km` : "",
    patientCondition: requestDetails?.patientCondition || "",
    status: requestDetails?.status || "",
    attachments: (requestDetails?.attachments || []).map((a) => {
      if (typeof a === "string") {
        const name = a.split("/").pop() || "file";
        return { type: getFileType({ name }), url: buildFileUrl(a), name };
      }
      const rawUrl = a.url || a.path || a.fileUrl || a.filePath || a.location || "";
      const name = a.name || rawUrl.split("/").pop() || "file";
      const type = getFileType({ name, mimeType: a.mimeType || a.mimetype || a.contentType || a.type });
      return { type, url: buildFileUrl(rawUrl), name };
    }),
    description: requestDetails?.description || "",
    nursePreferences: { languages: requestDetails?.languagePreferences || [], gender: (requestDetails?.genderPreferences || [])[0] || "" },
    quotation: requestDetails?.quotationURL ? [{ type: "pdf", name: "Quotation", url: requestDetails.quotationURL }] : [],
  };

  const getFormattedDate = (date) => {
    if (!date) return "-";
    try {
      return new Date(date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
      return "-";
    }
  };

  const feedbackRating = Number(session?.feedback?.rating) || 0;
  const normalizedFeedbackRating = Math.min(Math.max(feedbackRating, 0), 5);
  const feedbackComment = session?.feedback?.comment || "-";
  const apiObservationImages = (session?.medicalUploadedImages || [])
    .map((image) => {
      if (typeof image === "string") return buildFileUrl(image);
      return buildFileUrl(image?.url || image?.fileUrl || image?.filePath || image?.path || image?.location || "");
    })
    .filter(Boolean);
  const observationImages = apiObservationImages.length > 0 ? apiObservationImages : [Icons.profileimg];
  const nurseNotesText = session?.checkoutNotes || session?.notes || "";
  const hasNurseObservations = from !== "scheduled" && (apiObservationImages.length > 0 || nurseNotesText || from === "past" || session?.status === "CHECKED_OUT");
const handleDownloadObservations = async () => {
  const pdf = new jsPDF();

  let yPosition = 20;

  pdf.setFontSize(16);
  pdf.text("Homecare Staff Observations", 20, yPosition);

  yPosition += 15;


  // Add Images
  for (let i = 0; i < observationImages.length; i++) {
    try {
      const imageUrl = observationImages[i];

      const response = await fetch(imageUrl);
      const blob = await response.blob();

      const reader = new FileReader();

      const imageData = await new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });


      if (yPosition > 220) {
        pdf.addPage();
        yPosition = 20;
      }


      pdf.setFontSize(12);
      pdf.text(
        `Observation Image ${i + 1}`,
        20,
        yPosition
      );

      yPosition += 10;


      pdf.addImage(
        imageData,
        "JPEG",
        20,
        yPosition,
        160,
        100
      );


      yPosition += 115;

    } catch(error) {
      console.error(
        "Image download failed",
        error
      );
    }
  }


  // Add Notes
  if (nurseNotesText) {

    if (yPosition > 230) {
      pdf.addPage();
      yPosition = 20;
    }


    pdf.setFontSize(14);

    pdf.text(
      "Homecare Staff Notes",
      20,
      yPosition
    );


    yPosition += 10;


    pdf.setFontSize(11);


    const notes = pdf.splitTextToSize(
      nurseNotesText,
      170
    );


    pdf.text(
      notes,
      20,
      yPosition
    );
  }


  pdf.save(
    "homecare-staff-observations.pdf"
  );
};

  return (
    <>
      <div className="pdl-container session-details-wrapper">
        <Widgets.BreadCrumb breadcrumbs={breadcrumbs} paths={paths} />

        {/* Header */}
        <div className="department-heading pdl">
          <div>{t("visitDetails")}</div>
          <div className="pdl-buttons">
            <div className="pdl-btn verified pav-status" style={{ backgroundColor: "#e6fffa", color: "#00b894", border: "none" }}>
              {t(session?.status) || session?.status || "-"}
            </div>
            <span className="req-id-text">
              {t("request_id")}: {session?.nursingRequest?.nursingRequestID || "-"}
            </span>
          </div>
        </div>

        {/* ── Scheduled tab: Session Details UI (no nurse) ── */}
        {from === "scheduled" && (
          <div className="pdl-info">
            <div className="pdl-header-row">
              <p className="pdl-title">{t("sessionDetails")}</p>
            </div>

            <div className="pdl-body grid-3-col">
              {/* Start Date */}
              <div className="field-item">
                <span className="session-heading" style={{ color: "#009688" }}>
                  {t("startDate")}
                  <span className="asterik">*</span>
                </span>
                <input
                  type="date"
                  value={schedStartDate}
                  min={todayISO}
                  onChange={(e) => setSchedStartDate(e.target.value)}
                  style={{
                    height: "3.6rem",
                    padding: "0 1rem",
                    borderRadius: "2rem",
                    border: "1px solid var(--box-border)",
                    background: "var(--main-bg)",
                    fontSize: "1.3rem",
                    width: "100%",
                    outline: "none",
                    cursor: "pointer",
                  }}
                />
              </div>

              {/* End Date */}
              <div className="field-item">
                <span className="session-heading" style={{ color: "#009688" }}>
                  {t("endDate")}
                  <span className="asterik">*</span>
                </span>
                <input
                  type="date"
                  value={schedEndDate}
                  min={schedStartDate}
                  onChange={(e) => setSchedEndDate(e.target.value)}
                  style={{
                    height: "3.6rem",
                    padding: "0 1rem",
                    borderRadius: "2rem",
                    border: "1px solid var(--box-border)",
                    background: "var(--main-bg)",
                    fontSize: "1.3rem",
                    width: "100%",
                    outline: "none",
                    cursor: "pointer",
                  }}
                />
              </div>

              {/* Session Hours — editable */}
              <div className="field-item">
                <span className="session-heading" style={{ color: "#009688" }}>
                  {t("sessionHours")}
                </span>
                <div className="field-value time-row" style={{ gap: "0.6rem" }}>
                  <TimePicker value={schedStartTime} onChange={setSchedStartTime} minMinutes={schedStartDate === todayISO ? nowMinutes : null} />
                  <span className="time-arrow">→</span>
                  <TimePicker value={schedEndTime} onChange={setSchedEndTime} />
                </div>
              </div>

              <div className="field-item">
                <span className="session-heading">{t("frequency")}</span>
                <div className="field-value" style={{ flexDirection: "column", gap: "0.6rem" }}>
                  <div style={{ display: "flex", gap: "0.8rem", flexWrap: "wrap" }}>
                    {DAY_KEYS.map((day, i) => (
                      <label key={day} style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: "1.3rem", cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={frequencyDays[i]}
                          onChange={() => {
                            const u = [...frequencyDays];
                            u[i] = !u[i];
                            setFrequencyDays(u);
                          }}
                          style={{ accentColor: "#009688", width: "1.4rem", height: "1.4rem" }}
                        />
                        {t(day)}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="service-type-main2">
              <div className="service-input-box">
                <span>{t("serviceGroup")}</span>
                <input type="text" className="form-section-input-2" value={session?.service?.title?.[lang] || session?.service?.title?.en || ""} readOnly />
              </div>
              <div className="service-input-box">
                <span>{t("subServiceGroup")}</span>
                <input type="text" className="form-section-input-2" value={session?.subService?.title?.[lang] || session?.subService?.title?.en || ""} readOnly />
              </div>
              <div className="service-input-box">
                <span>{t("serviceItem")}</span>
                <input type="text" className="form-section-input-2" value={session?.serviceItem?.title?.[lang] || session?.serviceItem?.title?.en || ""} readOnly />
              </div>
            </div>
          </div>
        )}

        {/* ── Upcoming/Past: Session Info Card ── */}
        {from !== "scheduled" && session && (
          <div className="pdl-info">
            <div className="pdl-header-row">
              <p className="pdl-title">{t("sessionDetails")}</p>
              {session.dayDetail && <span style={{ fontSize: "1.3rem", color: "#009688", fontWeight: 600 }}>{session.dayDetail}</span>}
            </div>
            <div className="pdl-body grid-3-col">
              <div className="field-item">
                <span className="session-heading">{t("sessionDate")}</span>
                <div className="field-value">{formatSessionDate(session.startTime || session.date)}</div>
              </div>
              <div className="field-item">
                <span className="session-heading">{t("sessionHours")}</span>
                <div className="field-value">
                  {formatSessionTime(session.startTime)} → {formatSessionTime(session.endTime)}
                  {calcDuration(session.startTime, session.endTime) && (
                    <span style={{ marginLeft: "0.6rem", color: "#888", fontSize: "1.2rem" }}>({calcDuration(session.startTime, session.endTime)})</span>
                  )}
                </div>
              </div>
              <div className="field-item">
                <span className="session-heading">{t("dayno")}</span>
                <div className="field-value">{session.dayDetail}</div>
              </div>
              <div className="field-item">
                <span className="session-heading">{t("serviceGroup")}</span>
                <div className="field-value">{session.service?.title?.[lang] || session.service?.title?.en || "-"}</div>
              </div>
              <div className="field-item">
                <span className="session-heading">{t("subServiceGroup")}</span>
                <div className="field-value">{session.subService?.title?.[lang] || session.subService?.title?.en || "-"}</div>
              </div>
              <div className="field-item">
                <span className="session-heading">{t("serviceItem")}</span>
                <div className="field-value">{session.serviceItem?.title?.[lang] || session.serviceItem?.title?.en || "-"}</div>
              </div>

              {session.checkInTime && (
                <div className="field-item">
                  <span className="session-heading">{t("checkInTime")}</span>
                  <div className="field-value">{formatSessionTime(session.checkInTime)}</div>
                </div>
              )}
              {session.checkOutTime && (
                <div className="field-item">
                  <span className="session-heading">{t("checkOutTime")}</span>
                  <div className="field-value">{formatSessionTime(session.checkOutTime)}</div>
                </div>
              )}
            </div>
            {/* {session.checkoutNotes && (
            <div style={{ marginTop: "1.2rem" }}>
              <span className="session-heading">{t("checkoutNotes")}</span>
              <div className="field-value" style={{ marginTop: "0.4rem", padding: "0.8rem 1rem", background: "var(--list-bg)", borderRadius: "0.8rem", fontSize: "1.3rem" }}>
                {session.checkoutNotes}
              </div>
            </div>
          )} */}
            {/* {session.feedback && (
            <div className="session-feedback-block">
              <div className="feedback-layout session-feedback-layout">
                <div className="feedback-col">
                  <span className="session-heading">{t("rating")}</span>
                  <div className="field-value session-rating-value">
                    <span className="star-rating-display" aria-label={`${normalizedFeedbackRating} ${t("rating")}`}>
                      {Array.from({ length: 5 }, (_, index) => (
                        <span key={index} className={`star ${index < normalizedFeedbackRating ? "filled" : ""}`} aria-hidden="true">
                          &#9733;
                        </span>
                      ))}
                    </span>
                    <span className="rating-text">{normalizedFeedbackRating ? `${normalizedFeedbackRating}/5` : "-"}</span>
                  </div>
                </div>
                <div className="feedback-col">
                  <span className="session-heading">{t("comment")}</span>
                  <div className="field-value session-comment-value">{feedbackComment}</div>
                </div>
              </div>
            </div>
          )} */}
          </div>
        )}

        {/* ── Upcoming/Past: Full Request Details (approved tab UI) ── */}
        {from !== "scheduled" && requestDetails && false && (
          <>
            {/* Patient Information */}
            <div className="whp-container ard-patient-card">
              <p className="ard-section-title">{t("patientInformation")}</p>
              <div className="ard-patient-body">
                <div className="ard-patient-left">
                  <img src={reqData.profilePhoto || Icons.noProfile} alt="profile" className="ard-profile-img" />
                  <div className="ard-hn-block">
                    <p className="ard-hn-label">
                      {t("hnNumber")}
                      <span className="asterik">*</span>
                    </p>
                    <div className="ard-hn-value">{reqData.hn || "-"}</div>
                  </div>
                </div>
                <div className="ard-patient-right">
                  <p className="ard-patient-name">{reqData.patientName}</p>
                  <div className="ard-info-row">
                    <div className="ard-info-item">
                      <img src={Icons.gender} alt="gender" />
                      <span>{reqData.gender ? t(reqData.gender) : "-"}</span>
                    </div>
                    <div className="ard-info-item">
                      <img src={Icons.CalenderIcon} alt="dob" />
                      <span>{getFormattedDate(reqData.dob)}</span>
                    </div>
                  </div>
                  <div className="ard-info-row">
                    <div className="ard-info-item">
                      <img src={Icons.Call} alt="phone" />
                      <span>{reqData.phone}</span>
                    </div>
                    <div className="ard-info-item">
                      <img src={Icons.SmsIcon} alt="email" />
                      <span>{reqData.email}</span>
                    </div>
                  </div>
                  <div className="ard-info-item ard-insurance-row">
                    <img src={Icons.insurance} alt="ins" />
                    <span>{reqData.insuranceCategory ? t(reqData.insuranceCategory) : "-"}</span>
                  </div>
                  {reqData.patientCondition && (
                    <div className="ard-condition-row">
                      <p className="ard-field-label">{t("patientCondition")}</p>
                      <div className="form-section-input-2" style={{ display: "flex", alignItems: "center", opacity: 0.8 }}>
                        {reqData.patientCondition}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="ard-address-zone-row">
                <div className="ard-address-col">
                  <div className="ard-info-item ard-address-head">
                    <img src={Icons.Location} alt="loc" />
                    <span className="ard-field-label-green">{t("patientAddress")}</span>
                  </div>
                  <p className="ard-address-text">{reqData.address}</p>
                </div>
                {reqData.zone && (
                  <div className="ard-zone-col">
                    <p className="ard-field-label">{t("zone")}</p>
                    <div className="ard-zone-value">{reqData.zone}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Attachments */}
            {reqData.attachments.length > 0 && (
              <div className="whp-container ard-attachments-card">
                <p className="ard-section-title">{t("attachments")}</p>
                <div className="attachments-grid-readonly">
                  {reqData.attachments.map((att, i) => (
                    <div key={i} className="attachment-item-readonly">
                      <div className="attachment-preview-readonly" onClick={() => openSecureFileUrl(att.url)}>
                        {att.type !== "image" ? (
                          <img src={Icons.PdfRedIcon} className="pdf-icon" alt="PDF" />
                        ) : (
                          <img
                            src={att.url}
                            className="attachment-image-fit"
                            alt={att.name}
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        )}
                      </div>
                      <span className="attachment-name">{att.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="whp-container ard-attachments-card">
              {/* Description */}
              {reqData.description && (
                <div className="ard-description-section">
                  <p className="ard-section-title">{t("description")}</p>
                  <div className="ard-description-text">
                    <p>{reqData.description}</p>
                  </div>
                </div>
              )}

              {/* Quotation */}
              {reqData.quotation.length > 0 && (
                <div className="ard-quotation-section">
                  <p className="ard-section-title">{t("quotation")}</p>
                  <div className="ard-quotation-list">
                    {reqData.quotation.map((f, i) => (
                      <div key={i} className="quotation-pdf-display" onClick={() => openSecureFileUrl(f.url)}>
                        <img src={Icons.PdfRedIcon} className="pdf-icon" alt="PDF" />
                        <span className="attachment-name">{f.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Homecare Staff Details — only for upcoming/past, not scheduled, and only when a nurse is assigned */}
        {from !== "scheduled" && nurseEntry && (
          <div className="pdl-info">
            <div className="pdl-header-row">
              <p className="pdl-title">{t("nurseDetails")}</p>
              <button
                className="chat-with-nurse-btn"
                onClick={() =>
                  setChatNurseId({
                    nurseId,
                    name: displayNurseName,
                    employeeID: nurseDetails?.employeeID || nurseEntry?.employeeID,
                    profilePhoto: nurseDetails?.profilePhoto || nurseEntry?.profilePhoto,
                  })
                }
              >
                💬 {t("chatWithNurse") || "Chat with Nurse"}
              </button>
            </div>
            <div className="pdl-body">
              <div className="pdl-section-leftt">
                <img
                  src={nurseDetails?.profilePhoto || Icons.profile}
                  alt="nurse"
                  className="nurse-avatar-lg"
                  onError={(e) => {
                    e.currentTarget.src = Icons.profile;
                  }}
                />
              </div>
              <div className="pdl-section-right">
                <p className="ppi-name">{nurseLoading ? t("loading") + "…" : displayNurseName}</p>
                <div className="nurse-info-grid">
                  <div className="pdl-detail">
                    <img src={Icons.gender} alt="gender" />
                    <span>{nurseDetails?.gender ? t(nurseDetails.gender) : "-"}</span>
                  </div>
                  <div className="pdl-detail">
                    <img src={Icons.id} alt="id" />
                    <span>{nurseDetails?.employeeID || "-"}</span>
                  </div>
                  <div className="pdl-detail">
                    <img src={Icons.SmsIcon} alt="email" />
                    <span>{nurseDetails?.email || "-"}</span>
                  </div>
                  <div className="pdl-detail">
                    <img src={Icons.Call} alt="phone" />
                    <span>
                      {nurseDetails?.countryCode ? `+${nurseDetails.countryCode} ` : ""}
                      {nurseDetails?.phoneNumber || "-"}
                    </span>
                  </div>
                  {nurseDetails?.address && (
                    <div className="pdl-detail nurse-address-row">
                      <img src={Icons.Location} alt="address" />
                      <span>
                        {typeof nurseDetails.address === "string"
                          ? nurseDetails.address
                          : nurseDetails.address?.en ||
                            nurseDetails.address?.vi ||
                            [nurseDetails.address?.street, nurseDetails.address?.ward, nurseDetails.address?.city].filter(Boolean).join(", ") ||
                            "-"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Homecare Staff Observations — upcoming/past only */}
      {hasNurseObservations && (
        <div className="pdl-info nurse-observations-card">
          <div className="pdl-header-row">
            <p className="pdl-title">{t("nursesObservations")}</p>
            {(observationImages.length > 0 || nurseNotesText) && (
  <button 
    type="button" 
    className="download-btn-text"
    onClick={handleDownloadObservations}
  >
    {t("download")}
  </button>
)}
          </div>
          <div className="pdl-body column-layout">
            {observationImages.length > 0 && (
              <div className="observation-carousel">
                <div className="carousel-track">
                  {observationImages.map((img, idx) => (
                    <button key={idx} type="button" className="obs-thumb-button" onClick={() => openSecureFileUrl(img)} aria-label={t("nursesObservations")}>
                      <img src={Icons.noProfile} alt={t("nursesObservations")} className="obs-thumb" />
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="notes-container">
              <div className="green-bar" />
              <div className="notes-content">
                <h4 className="notes-title">{t("nurseNotes")}</h4>
                <p className="notes-text">{nurseNotesText || "-"}</p>
              </div>
            </div>
          </div>
        </div>
      )}

        {/* Feedbacks — upcoming/past only */}
        {from !== "scheduled" && (session?.feedback || sessionRating) && (
          <div className="pdl-info">
            <p className="pdl-title">{t("feedbacks")}</p>
            <div style={{ display: "flex", gap: "0", width: "100%", alignItems: "flex-start" }}>
              {/* LEFT: Patient → Nurse Feedback */}
              <div style={{ flex: 1, paddingRight: "2.4rem" }}>
                <p style={{ fontWeight: 700, fontSize: "1.5rem", marginBottom: "0.2rem" }}>{t("patient")}</p>
                <p style={{ fontSize: "1.2rem", color: "#888", marginBottom: "1.6rem" }}>{t("feedback")}</p>

                <div style={{ display: "flex", alignItems: "center", gap: "0.8rem", marginBottom: "1.2rem", flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 700, fontSize: "1.4rem" }}>{t("ratingGiven")}:</span>
                  <span style={{ display: "flex", gap: "0.2rem" }}>
                    {Array.from({ length: 5 }, (_, i) => (
                      <span key={i} style={{ fontSize: "2rem", color: i < Math.min(Math.max(Number(session?.feedback?.rating) || 0, 0), 5) ? "#f5a623" : "#ddd" }}>
                        &#9733;
                      </span>
                    ))}
                  </span>
                </div>

                <p style={{ fontWeight: 700, fontSize: "1.4rem", marginBottom: "0.6rem" }}>{t("comment")}</p>
                <p style={{ fontSize: "1.3rem", color: session?.feedback?.comment ? "#444" : "#aaa", lineHeight: 1.6 }}>{session?.feedback?.comment || t("noCommentProvided")}</p>
              </div>

              {/* Divider */}
              <div style={{ width: "2px", background: "#009688", borderRadius: "2px", alignSelf: "stretch", flexShrink: 0 }} />

              {/* RIGHT: Nurse → Session Rating */}
              <div style={{ flex: 1, paddingLeft: "2.4rem" }}>
                <p style={{ fontWeight: 700, fontSize: "1.5rem", marginBottom: "0.2rem" }}>{t("homecareStaff")}</p>
                <p style={{ fontSize: "1.2rem", color: "#888", marginBottom: "1.6rem" }}>{t("feedback")}</p>

                {sessionRating ? (
                  <>
                    {/* Metric rows */}
                    {[
                      { key: "patientSatisfaction", label: t("patientSatisfaction") },
                      { key: "anxietyLevel", label: t("anxietyLevel") },
                      { key: "clearConnection", label: t("clearConnection") },
                      { key: "culturalSensitivity", label: t("culturalSensitivity") },
                      { key: "familyInvolvement", label: t("familyInvolvement") },
                      { key: "treatmentAdherence", label: t("treatmentAdherence") },
                    ].map(({ key, label }) => (
                      <div key={key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem", gap: "1rem" }}>
                        <span style={{ fontSize: "1.3rem", color: "#555", flex: 1 }}>{label}</span>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                          <div style={{ display: "flex", gap: "0.2rem" }}>
                            {Array.from({ length: 5 }, (_, i) => (
                              <span key={i} style={{ fontSize: "1.6rem", color: i < Math.min(Math.max(Number(sessionRating[key]) || 0, 0), 5) ? "#f5a623" : "#ddd" }}>
                                &#9733;
                              </span>
                            ))}
                          </div>
                          <span style={{ fontSize: "1.3rem", fontWeight: 600, color: "#333", minWidth: "2.5rem" }}>{sessionRating[key] ?? "-"}/5</span>
                        </div>
                      </div>
                    ))}

                    {/* Note */}
                    <div style={{ marginTop: "1.2rem" }}>
                      <p style={{ fontWeight: 700, fontSize: "1.4rem", marginBottom: "0.6rem" }}>{t("comment")}</p>
                      <p style={{ fontSize: "1.3rem", color: sessionRating.note ? "#444" : "#aaa", lineHeight: 1.6 }}>{sessionRating.note || t("noCommentProvided")}</p>
                    </div>
                  </>
                ) : (
                  <p style={{ fontSize: "1.3rem", color: "#aaa" }}>{t("noRatingProvided")}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Save — calls schedule request API */}
        <div className="form-section-footer" style={{ marginTop: "2rem", paddingRight: "2rem" }}>
          <button className="form-section-cancel" onClick={() => nav(`${strings.navigation.manageNurseReq}?activeList=${backTab}`)}>
            {t("cancel")}
          </button>
          <button className="form-section-create" onClick={handleSave}>
            {t("save")}
          </button>
        </div>
      </div>
      <ChatWidget openConversation={chatNurseId} onOpenConversationHandled={() => setChatNurseId(null)} />
    </>
  );
};

export default SessionDetailsPage;
